import { useListEvents } from '@cocoonspace/sdk-js/domains/events/events'
import type { NextPage } from 'next'
import { Trans } from 'next-i18next'
import Image from 'next/image'
import { FindCocoonFloatingBtn } from '~/components/atoms/find-cocoon-floating-btn'
import { MainContainer } from '~/components/atoms/main-container'
import { Skeleton } from '~/components/ui/skeleton'
import { DashboardCard } from '~/domains/account/components/dashboard/dashboard-card'
import { FeaturedEventSection } from '~/domains/events/components/my-events/featured-event-section'
import { useFeaturedEvents } from '~/domains/events/hooks/use-featured-events.hook'
import { useExperiment } from '~/hooks/use-experiment'
import { useUserContext } from '~/providers/user-provider'
import { DashboardFavsCard } from './dashboard-favs-card'
import { DashboardInvoiceCard } from './dashboard-invoice-card'
import { DashboardLastEventCard } from './dashboard-last-event-card'
import { DashboardMyUsers } from './dashboard-my-users'
import { DashboardUpcomingEventsCard } from './dashboard-upcoming-events-card'
import { DashboardWalletCard } from './dashboard-wallet-card'

export const DashboardDeskCard = () => {
	const { userQuery } = useUserContext()

	return (
		<DashboardCard
			isLoading={userQuery.isLoading}
			title='Vous avez une réservation Desk ?'
			link={{
				label: 'Réserver un Desk',
				href: '/desks/paris',
			}}
			childrenClassName='pb-4'
			footerClassName='text-left z-10'
		>
			{'Retrouvez le détails de vos réservations Desk dans vos e-mails.'}

			<div className='absolute inset-0 top-auto flex items-end justify-end max-md:hidden'>
				<Image
					alt=''
					src='/images/dashboard/welcome.svg'
					height={130}
					width={100}
					className='mr-[-28px]'
				/>
			</div>
		</DashboardCard>
	)
}

const IntroComponent = ({ hasPastEvents }: { hasPastEvents: boolean }) => {
	const { userQuery } = useUserContext()
	const { event: featuredEvent } = useFeaturedEvents()

	if (!hasPastEvents && !featuredEvent) {
		return (
			<Trans
				parent='div'
				i18nKey='cwDashboard:noPastEventIntro'
				values={{
					first_name: userQuery.data?.first_name
						? ` ${userQuery.data?.first_name}`
						: '',
				}}
				className='font-bold text-2xl md:text-4xl'
			/>
		)
	}

	if (hasPastEvents && !featuredEvent) {
		return (
			<div className='col-span-3'>
				<div className='flex flex-col gap-y-4'>
					<Trans
						parent='p'
						i18nKey='myEvents:featuredEvent.noEventGreetings'
						values={{
							first_name: !!userQuery.data?.first_name
								? ` ${userQuery.data.first_name}`
								: null,
						}}
						className='font-bold text-4xl'
					/>
				</div>
			</div>
		)
	}

	return <FeaturedEventSection className='col-span-3' />
}

IntroComponent.skeleton = () => (
	<div className='flex flex-col gap-3.5 py-2'>
		<Skeleton className='h-[16px] w-[200px]' />

		<Skeleton className='h-[16px] w-[400px]' />
	</div>
)

export const DashboardPage: NextPage = () => {
	const { userQuery, accountQuery } = useUserContext()

	const hasWalletExperiment = useExperiment('wallet')

	const { data: hasPastEvents, isLoading: isPastEventLoading } = useListEvents(
		{ include: 'past' },
		{
			query: {
				select: (events) => !!events.data.length,
			},
		},
	)

	const isBusiness = !!accountQuery.data?.business
	const permissions = userQuery.data?.permissions

	return (
		<MainContainer>
			<div className='flex flex-col gap-6 md:grid md:auto-rows-min md:grid-cols-3 md:gap-8'>
				<div className='col-span-3 grid grid-cols-1 gap-y-6 md:mb-auto md:gap-12'>
					{isPastEventLoading ? (
						<IntroComponent.skeleton />
					) : (
						<IntroComponent hasPastEvents={!!hasPastEvents} />
					)}
				</div>

				<DashboardUpcomingEventsCard />

				<DashboardInvoiceCard />

				{!hasWalletExperiment && (
					<>
						{isBusiness && !!permissions?.user_management_read ? (
							<DashboardMyUsers />
						) : (
							<DashboardFavsCard />
						)}
					</>
				)}

				{hasWalletExperiment && <DashboardWalletCard />}

				<DashboardLastEventCard />
				<DashboardDeskCard />
			</div>

			<FindCocoonFloatingBtn />
		</MainContainer>
	)
}
